// CLOUDWEB-21024: контракт postMessage-моста page -> SW для доставки Omicron-конфига.
// SW не видит window.cloudSettings (там нет window), поэтому FeaturesEs6 в нём всегда
// отдаёт falsy — фичи приезжают со страницы сообщением и кэшируются в IDB
// (serviceWorker/omicronConfig). Импортируется обеими сторонами как
// `serviceWorker/omicronConfigBridge` (алиас резолвится и в SW-, и в page-бандле).
import type { WebpushConfig } from 'reactApp/appHelpers/featuresHelpers/features/webPush';
import type { SwPlatform } from 'serviceWorker/platform';

// Page -> SW: снимок Omicron-фич web-пушей на момент загрузки страницы.
export const OMICRON_CONFIG_TYPE = 'CLOUD_SW_OMICRON_CONFIG';

export interface SwOmicronConfig {
    /** Значение Omicron-фичи `webpush-config` (или дефолт из features/webPush). */
    webpushConfig: WebpushConfig;
    /** Флаг Omicron-фичи `webpush-debug`. */
    webpushDebug: boolean;
    /**
     * Режим отображения страницы, приславшей снимок: установленное PWA приложение против вкладки.
     * В воркере не определяется никак — там нет ни `matchMedia`, ни `navigator.standalone`.
     * Оговорки про точность — у потребителя, в `serviceWorker/pushAnalytics`.
     */
    isStandalone: boolean;
    /**
     * Платформа устройства (CLOUDWEB-21080, §4): приезжает со страницы, потому что там определяется
     * точнее, чем в воркере по UA — `maxTouchPoints` есть только на `Navigator`, а SW видит
     * `WorkerNavigator`. Читает `getEffectiveSwPlatform()` через чистую `resolveSwPlatform(fromSnapshot,
     * ua)`; отдельного синхронного варианта по UA в воркере нет, чтобы замаскированный iPad не мог
     * попасть в решающую логику как `web`, а в разметку событий как `ios`.
     *
     * Поля может не быть: снимок пришёл со страницы предыдущей версии, где поля ещё не было, либо
     * это вообще первый визит и снимка в IDB нет. Штатный случай, а не ошибка — `resolveSwPlatform`
     * тогда падает на UA, то есть на сегодняшнее поведение.
     */
    platform?: SwPlatform;
    /**
     * Omicron-фича `webpush-deeplink-target`: у аккаунта есть нативка (Flexo-таргет, §4). Отдельным
     * полем, а не веткой внутри `webpushConfig`, потому что источник — не `webpush-config`, а
     * самостоятельная фича с собственным условием раскатки.
     *
     * Поля может не быть по той же причине, что и у `platform` (старый снимок, первый визит), и по
     * третьей: сам признак в Omicron работает через присутствие — «фичи нет» неотличимо от «юзер вне
     * таргета». В обоих случаях отсутствие означает «признака нет», а значит подавлять по нему нельзя
     * (fail-safe, §4) — вплоть до дефолта `skipDeeplinkPushWithoutTarget: false`.
     */
    deeplinkTarget?: boolean;
}

export interface OmicronConfigMessage {
    type: typeof OMICRON_CONFIG_TYPE;
    config: SwOmicronConfig;
    /** Момент снятия конфига на странице — для диагностики свежести кэша в SW. */
    ts: number;
}

// Page -> SW: «отдай то, что реально используешь». Спрашиваем воркер, а не читаем IDB со
// страницы: в памяти инстанса конфиг может быть свежее диска (например, запись не удалась),
// а версия скрипта из IDB не видна вообще.
export const OMICRON_CONFIG_REQUEST_TYPE = 'CLOUD_SW_OMICRON_CONFIG_REQUEST';

export interface OmicronConfigRequestMessage {
    type: typeof OMICRON_CONFIG_REQUEST_TYPE;
}

/** SW -> Page: ответ на запрос состояния. Приходит в `MessageChannel`-порт запроса. */
export interface OmicronConfigStateMessage {
    config: SwOmicronConfig | null;
    /** Когда снимок записан в IDB; `null` — конфига в воркере нет. */
    savedAt: number | null;
    /**
     * Ревизия реально исполняемого скрипта (хеш содержимого) — сразу видно, не остался ли
     * активным старый воркер. Соответствие «ревизия → коммит» лежит в
     * firebase-messaging-sw.version.json рядом с воркером.
     */
    swRevision: string;
}
